import { cn } from '@my/ui';
import { Check, Minus } from 'lucide-react';
import type { Requirement, StepTone, TimelineStep } from '../lib/bookingDetail';

// Small presentational bits shared by the booking panels (BookingExpand — the
// sailing picker; BookingDetails — the booked-booking form). Components-only so
// they can be reused without duplicating the look + feel.

const STEP_DOT: Record<StepTone, string> = {
  amber: 'bg-amber-500',
  blue: 'bg-blue-500',
  dark: 'bg-slate-700',
  red: 'bg-red-500',
  muted: 'bg-slate-300',
};

/** Vertical journey timeline — latest-into-port → … → delivery window. */
export function JourneyTimeline({
  routeLabel,
  warning,
  steps,
}: {
  routeLabel: string;
  warning: string | null;
  steps: TimelineStep[];
}) {
  return (
    <aside className="border-t border-slate-200 p-3 lg:border-l lg:border-t-0">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        Journey timeline
      </div>
      <div className="mb-2 text-xs font-medium text-slate-700">{routeLabel}</div>

      {warning && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] font-medium text-red-700">
          {warning}
        </div>
      )}

      <ol className="space-y-0">
        {steps.map((step, i) => (
          <li key={step.key} className="flex gap-2.5">
            <div className="flex w-3 flex-col items-center">
              <span className={cn('mt-1 h-2 w-2 shrink-0 rounded-full', STEP_DOT[step.tone])} />
              {i < steps.length - 1 && (
                <span
                  className={cn(
                    'w-px flex-1',
                    step.dashed ? 'border-l border-dashed border-slate-300' : 'bg-slate-200',
                  )}
                />
              )}
            </div>
            <div className="pb-3">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                {step.label}
              </div>
              <div className="text-sm font-semibold tabular-nums text-slate-800">{step.time}</div>
              <div className="text-[10px] text-slate-400">{step.sub}</div>
            </div>
          </li>
        ))}
      </ol>
    </aside>
  );
}

/**
 * Horizontal journey progress bar — done steps green, upcoming grey, with the
 * next step ringed (the "current position"). Done is decided by comparing each
 * step's absolute minute against the real wall-clock time.
 */
export function JourneyProgress({ steps }: { steps: TimelineStep[] }) {
  if (steps.length === 0) return null;
  const now = new Date();
  const nowAbs = now.getHours() * 60 + now.getMinutes();
  const firstUpcoming = steps.findIndex((s) => s.atMin > nowAbs);

  return (
    <div className="border-t border-slate-200 px-4 py-3">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        Journey
      </div>
      <ol className="flex items-start">
        {steps.map((s, i) => {
          const done = s.atMin <= nowAbs;
          const isCurrent = i === firstUpcoming;
          const prevDone = i > 0 && (steps[i - 1]?.atMin ?? Infinity) <= nowAbs;
          return (
            <li key={s.key} className="flex min-w-0 flex-1 flex-col items-center text-center">
              {/* connector line + dot */}
              <div className="flex w-full items-center">
                <span
                  className={cn('h-0.5 flex-1', i === 0 ? 'opacity-0' : prevDone ? 'bg-emerald-400' : 'bg-slate-200')}
                />
                <span
                  className={cn(
                    'h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white',
                    done ? 'bg-emerald-500' : 'bg-slate-300',
                    isCurrent && 'shadow-[0_0_0_3px] shadow-blue-200',
                  )}
                />
                <span
                  className={cn('h-0.5 flex-1', i === steps.length - 1 ? 'opacity-0' : done ? 'bg-emerald-400' : 'bg-slate-200')}
                />
              </div>
              <div className="mt-1 px-1">
                <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                  {s.label}
                </div>
                <div
                  className={cn(
                    'text-xs font-semibold tabular-nums',
                    done ? 'text-slate-800' : 'text-slate-500',
                  )}
                >
                  {s.time}
                </div>
                <div className="text-[9px] text-slate-400">{s.sub}</div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {title}
      </div>
      {children}
    </div>
  );
}

export function KV({ k, v, amber, red }: { k: string; v: string; amber?: boolean; red?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-[11px] text-slate-400">{k}</span>
      <span
        className={cn(
          'text-right text-[11px] font-medium tabular-nums',
          amber ? 'text-amber-600' : red ? 'text-red-600' : 'text-slate-700',
        )}
      >
        {v}
      </span>
    </div>
  );
}

export function ReqChip({ req }: { req: Requirement }) {
  const map = {
    ok: { icon: <Check className="h-3 w-3" />, cls: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    todo: { icon: <Minus className="h-3 w-3" />, cls: 'border-amber-200 bg-amber-50 text-amber-700' },
    na: { icon: <Minus className="h-3 w-3" />, cls: 'border-slate-200 bg-white text-slate-400' },
  } as const;
  const m = map[req.state];
  return (
    <div className={cn('flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium', m.cls)}>
      {m.icon}
      {req.label}
    </div>
  );
}

export function DocInput({
  label,
  side,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  side: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="mb-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
        <span className="text-[10px] text-slate-400">{side}</span>
      </div>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="mt-0.5 w-full rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
      />
    </div>
  );
}

export function Dot({ dark }: { dark?: boolean }) {
  return <span className={dark ? 'text-slate-300' : 'text-slate-500'}>·</span>;
}
